import React, {useEffect, useState} from 'react';
import {getLanguage, t} from 'react-switch-lang';
import {FormProvider, useForm} from 'react-hook-form';
import {Col, Row, Spinner} from 'react-bootstrap';
import SelectInfinite from '../../../../../components/FormFields/SelectInfinite';
import InputText from '../../../../../components/FormFields/InputText';
import InputDate from '../../../../../components/FormFields/inputDate/InputDate';
import {
  handlePasteAndValidate, maxDecimalNumber,
  onDecimalNumberInputChange, parseFormattedNumber, setFormattedDefault, testIsGreaterThanMin, testValidNumber,
} from '../../../../../utils/helpers';
import TextArea from '../../../../../components/FormFields/Textarea';
import * as yup from 'yup';
import {defaultMinValueZero} from '../../../../../utils/const';
import {yupResolver} from '@hookform/resolvers';
import {format, parseISO} from 'date-fns';
import {Toast} from '../../../../../utils/sweetAlert';
import {
  createAMLForm,
  editAMLForm,
  getAMLForm,
  getControllingOverallRiskAssessmentLevels,
} from '../../../../../services/externalizations/sectorAMLService';
import {useParams} from 'react-router-dom';
import {
  customStyles,
  handleCancelExternalization,
  isUserReviewer, statusesOfVerification,
} from '../../../../../utils/externalizationFormOptions';
import Swal from 'sweetalert2';
import {useExternalizationContext} from '../../../../../contexts/ExternalizationContext';
import {useUserContext} from '../../../../../contexts/UserContext';
import FormActionButtons from '../../../components/formActionButtons/FormActionButtons';
import VerifyExternalizationModal from '../../../components/verifyExternalizationModal/VerifyExternalizationModal';

const SectorAml = ({cycleId, isLastCycle, isEditAllowedByStatus, externalizationFormConfigData}) => {
  const [disabled, setDisabled] = useState(false);
  const [sectorAmlFormId, setSectorAmlFormId] = useState(false);
  const [loading,setLoading] = useState(false);
  const [isReview,setIsReview] = useState(false);
  const [populated,setPopulated] = useState(false);
  const [showEditBtn,setShowEditBtn] = useState(false);
  const [defaultData,setDefaultData] = useState([]);
  const[impactOnBankLiquidity, setImpactOnBankLiquidity] = useState('')
  const[impactOnBankLiquidityUpThreeMonths, setImpactOnBankLiquidityUpThreeMonths] = useState('')
  const [totalRiskAssessment, setTotalRiskAssessment] = useState([])
  const [riskDescription, setRiskDescription] = useState('')
  const [edit,setEdit] = useState(showEditBtn);
  const externalizationConfigData = useExternalizationContext();
  const user = useUserContext()
  const [E10, setE10] = useState(null)
  const [E13, setE13] = useState(null)
  const [E15, setE15] = useState(null)
  const [E16, setE16] = useState(null)
  const [E17, setE17] = useState(null)
  const {id} = useParams()
  const [responsibleUserId, setResponsibleUserId] = useState()
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verificationStatus, setVerificationStatus]= useState('')
  const canVerify = verificationStatus === 'PENDING'
  const userCanVerifyForm = user?.id === responsibleUserId
  const toggleVerifyModal = () => setVerifyModalOpen(prev => !prev);
  const schema = yup.object().shape({
    processingDate: yup.date()
      .nullable()
      .typeError(t('message.fieldRequired'))
      .required(t('message.fieldRequired')),
    averageTransactionBalanceLastPeriod: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .test('is-greater-than-min', t('message.minValue', {min: defaultMinValueZero}), (value) => {
        return testIsGreaterThanMin(value,defaultMinValueZero);
      })
      .test('is-valid-number', t('message.mustBeNumber'), (value) => {
        return testValidNumber(value,true);
      }).test('max-two-decimals', t('message.maxTwoDecimals'), (value) => {
        return maxDecimalNumber(value);
      })
      .typeError(t('message.onlyDecimalNumbers')),
    monthlyInvoiceAmountWithVAT: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .test('is-greater-than-min', t('message.minValue', {min: defaultMinValueZero}), (value) => {
        return testIsGreaterThanMin(value,defaultMinValueZero);
      })
      .test('is-valid-number', t('message.mustBeNumber'), (value) => {
        return testValidNumber(value,true);
      }).test('max-two-decimals', t('message.maxTwoDecimals'), (value) => {
        return maxDecimalNumber(value);
      })
      .typeError(t('message.onlyDecimalNumbers')),
    mandatoryReserveLastPeriod: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .test('is-greater-than-min', t('message.minValue', {min: defaultMinValueZero}), (value) => {
        return testIsGreaterThanMin(value,defaultMinValueZero);
      })
      .test('is-valid-number', t('message.mustBeNumber'), (value) => {
        return testValidNumber(value,true);
      }).test('max-two-decimals', t('message.maxTwoDecimals'), (value) => {
        return maxDecimalNumber(value);
      })
      .typeError(t('message.onlyDecimalNumbers')),
    comment: yup
      .string()
      .trim()
      .max(2000, t('message.max', {max: 2000})),
  });
  const methods = useForm({
    resolver: yupResolver(schema),
  });
  const lang = getLanguage();
  const {handleSubmit, setValue, trigger, watch} = methods;
  const watchAmountOfMonthlyInvoice = watch("monthlyInvoiceAmountWithVAT")
  const watchAverageBalanceOnTransactionAccount = watch("averageTransactionBalanceLastPeriod")
  const watchAllocatedRequiredReserve = watch("mandatoryReserveLastPeriod")
  const watchMonthlyBalanceToCashOutflowRatio= watch("monthlyBalanceToCashOutflowRatio");
  const watchMonthlyRequiredReserveToOutflowRatio= watch("monthlyRequiredReserveToOutflowRatio");
  const watchControllingOverallRiskAssessmentLevel= watch("controllingOverallRiskAssessmentLevel");
  const submitForm = async (data) => {
    const formatProcessingDate =
      format(new Date(data?.processingDate), 'yyyy-MM-dd');
    setDisabled(true);

    const dataToSubmit = {
      ...(edit && {
        id: defaultData?.id ? defaultData?.id : sectorAmlFormId,
      }),
      processingDate: formatProcessingDate,
      comment: data?.comment,
      averageTransactionBalanceLastPeriod: parseFormattedNumber(data?.averageTransactionBalanceLastPeriod),
      monthlyInvoiceAmountWithVAT: parseFormattedNumber(data?.monthlyInvoiceAmountWithVAT),
      monthlyBalanceToCashOutflowRatio: parseFormattedNumber(data?.monthlyBalanceToCashOutflowRatio),
      quarterlyBalanceToCashOutflowRatio: parseFormattedNumber(data?.quarterlyBalanceToCashOutflowRatio),
      mandatoryReserveLastPeriod: parseFormattedNumber(data?.mandatoryReserveLastPeriod),
      monthlyRequiredReserveToOutflowRatio: parseFormattedNumber(data?.monthlyRequiredReserveToOutflowRatio),
      quarterlyRequiredReserveToOutflowRatio: parseFormattedNumber(data?.quarterlyRequiredReserveToOutflowRatio),
      liquidityImpact: impactOnBankLiquidity?.value,
      liquidityImpactUpTo3Months: impactOnBankLiquidityUpThreeMonths?.value,
      controllingOverallRiskAssessmentLevel: {id: data?.controllingOverallRiskAssessmentLevel?.value},
      populated: true,
      cycle: {id: parseInt(cycleId)},
      formConfig: externalizationFormConfigData,
      userInCharge: {id: externalizationFormConfigData?.defaultDepartment?.userInChargeForExternalizationId},
      responsibleUser: {id: externalizationFormConfigData?.defaultDepartment?.userResponsibleForExternalizationId}

    };
    if(edit) {
      try{
        const res = await editAMLForm(defaultData?.id ? defaultData?.id : sectorAmlFormId,dataToSubmit)
        if (res?.status === 200) {
          Toast.fire({
            title: t('message.updateSuccess2', {
              entity: t('externalization.sectorAML.sectorAML'),
            }),
            icon: 'success',
          });
          const newVerificationStatus = res?.data?.verificationStatus;
          if (newVerificationStatus && newVerificationStatus !== verificationStatus) {
            setTimeout(() => {
              window.location.reload();
            }, 800);
          } else {
            setVerificationStatus(newVerificationStatus);
            setDisabled(true);
          }
        }
        setDisabled(true);
      } catch (err){
        if (err?.response?.data?.code === 'responsible-user-for-externalization-is-missing') {
          Swal.fire({
            text:
              lang === 'eng'
                ? err.response.data.description
                : t('message.responsibleUserForExternalizationMissing'),
            icon: 'error',
          });
          setDisabled(false)
          setEdit(false)
        } else {
          Toast.fire({
            title: t('message.errorApi'),
            icon: 'error',
          });
        }
      }
    }else {
      try {
        const response = await createAMLForm(dataToSubmit)
        if (response?.status === 201) {
          Toast.fire({
            title: t('message.updateSuccess2', {
              entity: t('externalization.sectorAML.sectorAML'),
            }),
            icon: 'success',
          });
          setDisabled(true)
          setEdit(true)
          setIsReview(true);
          setVerificationStatus('PENDING')
          setSectorAmlFormId(response?.data?.id)
          setResponsibleUserId(externalizationFormConfigData?.defaultDepartment?.userResponsibleForExternalizationId);
        }
      }      catch (err){
        if (err?.response?.data?.code === 'responsible-user-for-externalization-is-missing') {
          Swal.fire({
            text:
              lang === 'eng'
                ? err.response.data.description
                : t('message.responsibleUserForExternalizationMissing'),
            icon: 'error',
          });
          setDisabled(false)
          setEdit(false)
        } else {
          Toast.fire({
            title: t('message.errorApi'),
            icon: 'error',
          });
        }
      }
    }
  }
  useEffect(() => {
    async function fetchRiskLevels() {
      const {data} = await getControllingOverallRiskAssessmentLevels();
      const validLevels = data.filter(r => r.value >= 0 && r.value <= 2);
      setTotalRiskAssessment(validLevels);
    }
    fetchRiskLevels();
  }, []);
  function determineRiskValue(opLevel) {

    const levels = {
    "LOW": 0,
    "MEDIUM": 1,
    "HIGH": 2
  };
  const op = levels[opLevel];
  if (op === 0) {
    return 0;
  }
    else if (op === 1){
      return 1
  }
  return 2;
}

function getRiskAssessmentDTO(opLevel, riskLevelList) {
  const levelMap = { 0: "LOW", 1: "MEDIUM", 2: "HIGH" };
  const riskValue = determineRiskValue(levelMap[E17]);
  return riskLevelList.find(r => r.value === riskValue);
}
  useEffect(() => {
    if (watchControllingOverallRiskAssessmentLevel && totalRiskAssessment?.length > 0) {
      const matchedRisk = totalRiskAssessment.find(
        (item) => item.id === watchControllingOverallRiskAssessmentLevel?.value
      );
      if (matchedRisk) {
        setRiskDescription({
          value: matchedRisk?.value,
          label: lang === 'mne' ? matchedRisk?.description : matchedRisk?.descriptionEn,
        });
      }
    }
  }, [watchControllingOverallRiskAssessmentLevel, totalRiskAssessment]);
  useEffect(() => {
      getAMLForm(cycleId).then((res) => {
        const form = res?.data?.[0];
        if(form) {
          setDefaultData(form);
                setPopulated(!!form?.populated);
                setEdit(!!form?.populated);
                setDisabled(!!form?.populated);
          setVerificationStatus(form?.verificationStatus)
          setResponsibleUserId(externalizationFormConfigData?.defaultDepartment?.userResponsibleForExternalizationId)
          setIsReview(!!form?.populated && isUserReviewer(externalizationConfigData,user,'controllingForm'));
                setShowEditBtn(!form?.populated === false || !isUserReviewer(externalizationConfigData,user,'controllingForm'));
        }
        if(res.data[0]?.populated) {
          setPopulated(false)
          setEdit(true);
        }else {
          setValue('processingDate', new Date());
          setEdit(false)
          setDisabled(!isUserReviewer(externalizationConfigData,user,'controllingForm'));
        }
      })

  }, [id]);

  useEffect(() => {
    if (watchAmountOfMonthlyInvoice && watchAverageBalanceOnTransactionAccount) {
      if(Number(watchAmountOfMonthlyInvoice) !== defaultData.monthlyInvoiceAmountWithVAT) {

        const invoice = parseFormattedNumber(watchAmountOfMonthlyInvoice);
        const avgBalance = parseFormattedNumber(watchAverageBalanceOnTransactionAccount);
        const monthlyRatio = ((invoice / avgBalance) * 100).toFixed(2);
        const quarterlyRatio = (((invoice * 3) / avgBalance) * 100).toFixed(2);
        const formattedRatio =  monthlyRatio.toString().replace('.', ',');
        const formattedQuarterlyRation = quarterlyRatio.toString().replace('.',',')


        setFormattedDefault("monthlyBalanceToCashOutflowRatio", formattedRatio,setValue);
        setFormattedDefault('quarterlyBalanceToCashOutflowRatio',formattedQuarterlyRation,setValue);
      }
    }


    if (watchAmountOfMonthlyInvoice && watchAllocatedRequiredReserve) {
      if(Number(watchAmountOfMonthlyInvoice) !== defaultData.mandatoryReserveLastPeriod) {
        const invoice = parseFormattedNumber(watchAmountOfMonthlyInvoice);
        const allocatedReserve = parseFormattedNumber(watchAllocatedRequiredReserve);

        const monthlyReserveRatio = ((invoice / allocatedReserve) * 100).toFixed(2);
        const quarterlyReserveRatio = (((invoice * 3) / allocatedReserve) * 100).toFixed(2);

        const formattedMonthlyReserveRation =  monthlyReserveRatio.toString().replace('.', ',');
        const formattedQuarterlyReserveRatio = quarterlyReserveRatio.toString().replace('.',',');

        setFormattedDefault("monthlyRequiredReserveToOutflowRatio", formattedMonthlyReserveRation,setValue);
        setFormattedDefault('quarterlyRequiredReserveToOutflowRatio',formattedQuarterlyReserveRatio,setValue);
      }
    }
  }, [watchAmountOfMonthlyInvoice, watchAverageBalanceOnTransactionAccount, watchAllocatedRequiredReserve]);
  useEffect(() => {
    const monthlyRatio = parseFormattedNumber(watchMonthlyBalanceToCashOutflowRatio);
    const reserveRatio = parseFormattedNumber(watchMonthlyRequiredReserveToOutflowRatio);
    setE10(
      monthlyRatio != null && !isNaN(monthlyRatio)
        ? monthlyRatio <= 3
          ? 0
          : monthlyRatio <= 5
            ? 1
            : 2
        : null
    );
    setE13(
      reserveRatio != null && !isNaN(reserveRatio)
        ? reserveRatio <= 3
          ? 0
          : reserveRatio <= 5
            ? 1
            : 2
        : null
    );
  }, [watchMonthlyBalanceToCashOutflowRatio, watchMonthlyRequiredReserveToOutflowRatio]);
  useEffect(() => {
    if (E10 == null || E13 == null) {
      setE15(null);
      setE16(null);
      setE17(null);
      setImpactOnBankLiquidity(null);
      setImpactOnBankLiquidityUpThreeMonths(null);
      setValue('controllingOverallRiskAssessmentLevel', null);
      return;
    }

    const maxVal = Math.max(E10, E13);
    setE15(maxVal);
    setE16(maxVal);
    setE17(maxVal);
  }, [E10, E13, setValue]);

  useEffect(() => {
    if (E17 == null || totalRiskAssessment.length === 0) return;

    const riskAssessment = getRiskAssessmentDTO(E17, totalRiskAssessment);
    if(riskAssessment) {
      const transformed = {
        value: riskAssessment?.id,
        label: lang === 'mne' ? riskAssessment?.name : riskAssessment?.nameEn
      };
      setValue('controllingOverallRiskAssessmentLevel', transformed);
    }
  }, [E17, totalRiskAssessment, lang, setValue]);

  useEffect(() => {
    if (E15 === null || E16 === null) return;

    const risk =
      E15 === 0
        ? { value: 0, label: t('externalization.lowLevel') }
        : E15 === 1
          ? { value: 1, label: t('externalization.mediumLevel') }
          : { value: 2, label: t('externalization.highLevel') };

    const riskUp3M =
      E16 === 0
        ? { value: 0, label: t('externalization.lowLevel') }
        : E16 === 1
          ? { value: 1, label: t('externalization.mediumLevel') }
          : { value: 2, label: t('externalization.highLevel') };

    setImpactOnBankLiquidity(risk);
    setImpactOnBankLiquidityUpThreeMonths(riskUp3M);
  }, [E15, E16]);

  useEffect(() => {
    if (
      defaultData?.controllingOverallRiskAssessmentLevel &&
      totalRiskAssessment?.length > 0
    ) {
      setValue("controllingOverallRiskAssessmentLevel", {
        value: defaultData?.controllingOverallRiskAssessmentLevel?.id,
        label:
          lang === "mne"
            ? defaultData?.controllingOverallRiskAssessmentLevel?.name
            : defaultData?.controllingOverallRiskAssessmentLevel?.nameEn
      });
    }
    if(defaultData?.processingDate){
      setValue('processingDate',parseISO(defaultData?.processingDate))}

    if(defaultData?.averageTransactionBalanceLastPeriod) {
      setFormattedDefault('averageTransactionBalanceLastPeriod',defaultData?.averageTransactionBalanceLastPeriod,setValue);
    }
    if(defaultData?.monthlyInvoiceAmountWithVAT) {
      setFormattedDefault('monthlyInvoiceAmountWithVAT',defaultData?.monthlyInvoiceAmountWithVAT,setValue);
    }
    if(defaultData?.mandatoryReserveLastPeriod) {
      setFormattedDefault('mandatoryReserveLastPeriod',defaultData?.mandatoryReserveLastPeriod,setValue);
    }
  }, [defaultData, totalRiskAssessment, lang]);

  if (loading) {
    return (
        <div className="text-center mt-4 mb-4">
          <Spinner animation="border" role="status" />
        </div>
    );
  }
  return (
    <>
      <FormActionButtons
        isReview={isReview}
        edit={edit}
        disabled={disabled}
        isLastCycle={isLastCycle}
        isEditAllowedByStatus={isEditAllowedByStatus}
        userCanVerifyForm={userCanVerifyForm}
        canVerify={canVerify}
        toggleVerifyModal={toggleVerifyModal}
        setDisabled={setDisabled}
      />
      <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Row className="mb-4">
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.departments')}
                </label>
                <SelectInfinite
                  api="departments"
                  defaultValue={
                    externalizationFormConfigData?.defaultDepartment
                      ? {
                        value: externalizationFormConfigData?.defaultDepartment?.id,
                        label:
                          lang === 'mne'
                            ? externalizationFormConfigData?.defaultDepartment?.name
                            : externalizationFormConfigData?.defaultDepartment?.nameEn,
                      }
                      : null
                  }
                  name="departmentInCharge"
                  labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                  className="select"
                  isDisabled={true}
                />
                <label className="label w-100 mt-2">
                  {t('externalization.date')}
                </label>
                <InputDate
                  name="processingDate"
                  className={'date-input'}
                  defaultValue={defaultData?.processingDate !== undefined ? format(new Date(defaultData?.processingDate), 'yyyy-MM-dd') : new Date()}
                  disabled={disabled}
                />
              </Col>
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.averageTransactionBalanceLastPeriod')}
                </label>
                <InputText
                  type="text"
                  name="averageTransactionBalanceLastPeriod"
                  className="form-control input-field"
                  disabled={disabled}
                  defaultValue={defaultData?.averageTransactionBalanceLastPeriod}
                  placeholder={t('externalization.sectorAML.placeholder.averageTransactionBalanceLastPeriod')}
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "averageTransactionBalanceLastPeriod")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'averageTransactionBalanceLastPeriod')}
                />
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.monthlyInvoiceAmountWithVAT')}
                </label>
                <InputText
                  type="text"
                  name="monthlyInvoiceAmountWithVAT"
                  className="form-control input-field"
                  disabled={disabled}
                  defaultValue={defaultData?.monthlyInvoiceAmountWithVAT}
                  placeholder={t('externalization.sectorAML.placeholder.monthlyInvoiceAmountWithVAT')}
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "monthlyInvoiceAmountWithVAT")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'monthlyInvoiceAmountWithVAT')}
                />
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.monthlyBalanceToCashOutflowRatio')}
                </label>
                <InputText
                  type="text"
                  name="monthlyBalanceToCashOutflowRatio"
                  className="form-control input-field"
                  defaultValue={defaultData?.monthlyBalanceToCashOutflowRatio}
                  disabled
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "monthlyBalanceToCashOutflowRatio")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'monthlyBalanceToCashOutflowRatio')}
                />
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.quarterlyBalanceToCashOutflowRatio')}
                </label>
                <InputText
                  type="text"
                  name="quarterlyBalanceToCashOutflowRatio"
                  className="form-control input-field"
                  disabled={true}
                  defaultValue={defaultData?.quarterlyBalanceToCashOutflowRatio}
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "quarterlyBalanceToCashOutflowRatio")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'quarterlyBalanceToCashOutflowRatio')}
                />
              </Col>
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.mandatoryReserveLastPeriod')}
                </label>
                <InputText
                  disabled={disabled}
                  type="text"
                  name="mandatoryReserveLastPeriod"
                  defaultValue={defaultData?.mandatoryReserveLastPeriod}
                  className="form-control input-field"
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "mandatoryReserveLastPeriod")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'mandatoryReserveLastPeriod')}
                  placeholder={t('externalization.sectorAML.placeholder.mandatoryReserveLastPeriod')}
                />
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.monthlyRequiredReserveToOutflowRatio')}
                </label>
                <InputText
                  type="text"
                  name="monthlyRequiredReserveToOutflowRatio"
                  className="form-control input-field"
                  defaultValue={defaultData?.monthlyRequiredReserveToOutflowRatio}
                  disabled={true}
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "monthlyRequiredReserveToOutflowRatio")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'monthlyRequiredReserveToOutflowRatio')}
                />
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.quarterlyRequiredReserveToOutflowRatio')}
                </label>
                <InputText
                  disabled={true}
                  type="text"
                  name="quarterlyRequiredReserveToOutflowRatio"
                  defaultValue={defaultData?.quarterlyRequiredReserveToOutflowRatio}
                  className="form-control input-field"
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "quarterlyRequiredReserveToOutflowRatio")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'quarterlyRequiredReserveToOutflowRatio')}

                />
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.impactOnBankLiquidity')}
                </label>
                <InputText
                  name={impactOnBankLiquidity}
                  className="form-control input-field"
                  value={impactOnBankLiquidity?.label}
                  defaultValue={defaultData?.impactOnBankLiquidity}
                  type="text"
                  disabled={true}
                />
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.impactOnBankLiquidityUpThreeMonths')}
                </label>
                <InputText
                  name={impactOnBankLiquidityUpThreeMonths}
                  className="form-control input-field"
                  value={impactOnBankLiquidityUpThreeMonths?.label}
                  type="text"
                  defaultValue={defaultData?.impactOnBankLiquidityUpThreeMonths}
                  disabled={true}
                />
              </Col>
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.totalRiskAssessment')}
                </label>
                <SelectInfinite
                  options={totalRiskAssessment}
                  name="controllingOverallRiskAssessmentLevel"
                  labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                  className="select"
                  placeholder={''}
                  isDisabled={true}
                  styles={customStyles}
                  value={watchControllingOverallRiskAssessmentLevel}
                  />
                <label className="label w-100 mt-2">
                  {t('externalization.sectorAML.riskDescription')}
                </label>

                <textarea
                  key={riskDescription}
                  value={riskDescription?.label}
                  type="text"
                  defaultValue={lang === 'mne' ? defaultData?.controllingOverallRiskAssessmentLevel?.description : defaultData?.controllingOverallRiskAssessmentLevel?.descriptionEn}
                  disabled={true}
                  rows={4}
                />
                <label className="label w-100 mt-2">
                  {t('comments.comment')}
                  <span className="optional register line-height">
                  {t('common.optional')}
                </span>
                </label>
                <TextArea
                  name="comment"
                  defaultValue={defaultData?.comment}
                  className={`form-control text-area `}
                  placeholder={t('placeholder.comment')}
                  disabled={disabled}
                />
                <label className="label w-100 mt-2">
                  {t('externalization.analysisDoneBy')}
                </label>
                <InputText
                  type="text"
                  name="userInCharge"
                  className="form-control input-field"
                  disabled={true}
                  id="userInCharge"
                  defaultValue={externalizationFormConfigData?.defaultDepartment.userInChargeForExternalizationFullName}
                />
                {externalizationFormConfigData?.defaultDepartment?.deputyUserInChargeForExternalizationFullName && <>
                  <label className="label w-100 mt-2">{t('departments.userInChargeReplacementForExternalization')}</label>
                  <InputText
                    type="text"
                    name="userInChargeDeputy"
                    className="form-control input-field"
                    disabled={true}
                    id="userInCharge"
                    defaultValue={externalizationFormConfigData?.defaultDepartment?.deputyUserInChargeForExternalizationFullName}
                  /></>}
                <label className="label w-100 mt-2">
                  {t('externalization.verificationDoneBy')}
                </label>
                <InputText
                  type="text"
                  name="responsibleUser"
                  className="form-control input-field"
                  disabled={true}
                  id="responsibleUser"
                  defaultValue={externalizationFormConfigData?.defaultDepartment.userResponsibleForExternalizationFullName}
                />
                  {edit && <>  <label className="label w-100 mt-2">{t('externalization.verificationStatus')}</label>
                    <InputText
                      name="verificationStatus"
                      type="text"
                      className="form-control input-field"
                      placeholder={t( 'externalization.placeholder.verificationStatus')}
                      disabled={true}
                      defaultValue={statusesOfVerification.find(
                        (status) => status.value === verificationStatus)?.label}
                    /></>}
              </Col>
            </Row>
            {(!edit || (edit && !disabled)) && isUserReviewer(externalizationConfigData, user, 'controllingForm') &&<Row className="mt-5 mb-3 form-footer">
              <button
                className="secondary-btn"
                type="reset"
                onClick={handleCancelExternalization}>
                {t('buttons.cancel')}
              </button>
              <button className="primary-btn" disabled={disabled}>{t('buttons.save')}</button>
            </Row>}
          </form>
        </FormProvider>
      <VerifyExternalizationModal
        open={verifyModalOpen}
        formType={externalizationFormConfigData?.formKey}
        formId={defaultData?.id ? defaultData?.id : sectorAmlFormId}
        isOpen={setVerifyModalOpen}
      />
    </>
  );
};

export default SectorAml;